import { DateValue } from '@givebutter/primitives/date-picker';
import { pluralize } from '@givebutter/util';
import {
    ConditionalOperator,
    FilterGroup,
    FilterOperator,
    FilterRequestValue,
    ServerKey,
    UnmaskFn,
} from 'components/filter/types';
import { SHORT_DATE_FORMAT, formatDate } from 'helpers/dates';
import { first, omit } from 'lodash';
import { DateTime } from 'luxon';
import { ColumnSchemaFilter, ReportResource } from 'types/domain';

const deepSanitizeFilterGroups = (groups: FilterGroup[]) =>
    groups
        .filter(f => f.filters.length > 0)
        .map(f => omit(f, ['id']).filters?.map(f => omit(f, ['id'])));

const getFilterCounts = (groups: FilterGroup[]) => {
    const totalFilters = groups.reduce((prev, curr) => {
        curr.filters.forEach(_ => (prev = prev + 1));
        return prev;
    }, 0);
    const filtersCountReadable = `${totalFilters} ${pluralize(totalFilters, 'filter')}`;

    return {
        totalFilters,
        filtersCountReadable,
    };
};

const KEY_PREFIX = 'dynamic';

const prefixDynamicFilter = (key: ServerKey) => `${KEY_PREFIX}_${key}` as ServerKey;

const rawServerKeyFromPrefixedFilter = (key: string) => {
    const regex = new RegExp(`^${KEY_PREFIX}_`);
    const match = regex.exec(key);

    return match ? key.slice(match[0].length) : key;
};

const COLUMN_KEY_PREFIX = 'tableColumn';

const getColumnFilterKey = (
    columnName: string,
    filterType: ColumnSchemaFilter['type'],
    resource: ReportResource,
) => `${COLUMN_KEY_PREFIX}_${resource}_${columnName}_${filterType}` as ServerKey;

const isRelativeDateOperator = (operator: FilterOperator) => {
    switch (operator) {
        case 'TODAY':
        case 'YESTERDAY':
        case 'LAST-WEEK':
        case 'LAST-TWO-WEEKS':
        case 'LAST-MONTH':
        case 'LAST-QUARTER':
        case 'LAST-YEAR':
        case 'MTD':
        case 'QTD':
        case 'YTD':
        case 'ALL-TIME':
        case 'CUSTOM':
            return true;
        default:
            return false;
    }
};

const multiSelectToSingle = <T>(value: unknown) =>
    (Array.isArray(value) ? value.join('') : value) as T;

const injectOperator = (
    filter: Omit<FilterRequestValue<unknown>, 'type'>,
    operator: ConditionalOperator,
): FilterRequestValue<unknown> => ({
    ...filter,
    type: operator,
});

const injectEquals = (
    filter: Omit<FilterRequestValue<unknown>, 'type' | 'operator'>,
): Omit<FilterRequestValue<unknown>, 'type'> => ({
    ...filter,
    operator: '=',
});

const transformDateToRequest = (
    date: DateValue,
    filter: ConditionalOperator,
    operator?: FilterOperator,
    inferTz: boolean = true,
): FilterRequestValue<string | string[]> & { tz?: string } => {
    const coercedDate = Array.isArray(date) ? date : [date];
    const [startDate] = coercedDate;
    const tz = inferTz ? startDate?.zoneName : undefined;

    const value = coercedDate.map(value => (value ? formatDate(value, SHORT_DATE_FORMAT) : ''));
    // the BE won't accept a single element array for operators that aren't "bet"
    const valueBasedOnOperator = operator === 'bet' ? value : (first(value) ?? '');

    return {
        value: valueBasedOnOperator,
        type: filter,
        operator: transformOperatorForRequest(operator),
        tz,
    };
};

const transformValueToRequest = <T, K = T>(
    value: T,
    filter: ConditionalOperator,
    operator?: FilterOperator,
    unmaskFn?: UnmaskFn<T, K>,
): FilterRequestValue<T> => {
    return {
        value: (unmaskFn ? unmaskFn(value) : value) as T,
        type: filter,
        operator: transformOperatorForRequest(operator),
    };
};

const transformNullableDateToRequest = (
    date: DateValue | null,
    filter: ConditionalOperator,
    operator?: FilterOperator,
): FilterRequestValue<string | string[] | null> & { tz?: string } => {
    if (date === null) {
        return {
            value: null,
            type: filter,
            operator: transformOperatorForRequest(operator),
        };
    }
    return transformDateToRequest(date, filter, operator);
};

const transformOperatorForRequest = (operator?: FilterOperator) => {
    switch (operator) {
        case 'lt':
            return '<';
        case 'lte':
            return '<=';
        case 'gt':
            return '>';
        case 'gte':
            return '>=';
        case 'eq':
            return '=';
        case 'neq':
            return '<>';
        case 'bet':
            return 'between';
        case 'is_empty':
        case 'is_not_empty':
        case 'includes':
        case 'excludes':
        case 'all':
        case 'any':
        case 'none':
        default:
            return operator;
    }
};

const transformInboundDate = (date: string) => DateTime.fromFormat(date, SHORT_DATE_FORMAT);

export {
    deepSanitizeFilterGroups,
    getFilterCounts,
    prefixDynamicFilter,
    rawServerKeyFromPrefixedFilter,
    getColumnFilterKey,
    isRelativeDateOperator,
    multiSelectToSingle,
    injectEquals,
    injectOperator,
    transformOperatorForRequest,
    transformValueToRequest,
    transformDateToRequest,
    transformNullableDateToRequest,
    transformInboundDate,
};
